// frontend/src/farm/farmMarket.mjs
// What the farm market shows: tabs by crop kind, pages of 8 cards, each card's lock, mastery
// stars and progress to the next star. Pure (node --test); FarmMarket.js only draws it.
import { humanizeRemaining, masteredSellPrice } from './farmRules.mjs';
import { plural } from './vehicles.mjs';

export const TABS = [
  { id: 'wszystko', label: 'Wszystko' },
  { id: 'warzywa', label: 'Warzywa' },
  { id: 'owoce', label: 'Owoce' },
  { id: 'zboza', label: 'Zboża' },
  { id: 'kwiaty', label: 'Kwiaty' },        // was 'inne' (owner 2026-10-20): Słonecznik and the seven flowers
  { id: 'drzewa', label: 'Drzewa' },
  { id: 'dekoracje', label: 'Dekoracje' },
  { id: 'budowle', label: 'Budowle' },
  { id: 'narzedzia', label: 'Narzędzia' },
  { id: 'zwierzeta', label: 'Zwierzęta' },
];
// Two rows (owner 2026-09-29): the crops, then everything else; Kwiaty and Drzewa swapped (owner 2026-10-20).
export const TAB_ROWS = [TABS.slice(0, 5), TABS.slice(5)];
export const PAGE_SIZE = 8;

// "Nowość" on a tab until it is opened once (owner 2026-10-20). Per device in localStorage; a storage
// that throws still shows it, and opening the tab hides it for the rest of the session.
const NEW_TABS = { kwiaty: 'sd_seen_kwiaty' };
const seenThisSession = new Set();
export function _resetSeenForTests() { seenThisSession.clear(); }
// The page's localStorage, or null where reading it throws (site data blocked: SecurityError on the accessor itself).
export function siteStorage(scope = globalThis) {
  try { return scope.localStorage ?? null; } catch { return null; }
}
export function tabIsNew(id, storage) {
  const key = NEW_TABS[id];
  if (!key || seenThisSession.has(id)) return false;
  try { return storage?.getItem(key) !== '1'; } catch { return true; }
}
export function markTabSeen(id, storage) {
  const key = NEW_TABS[id];
  if (!key) return;
  seenThisSession.add(id);
  try { storage?.setItem(key, '1'); } catch { /* private mode: the session memory is enough */ }
}

// A remembered seed is usable only if the catalog has it and the player is at its level (a crop can move
// to a later level — Kwiaty ladder 2026-10-20).
export function seedUsable(catalog, id, level) {
  const c = catalog?.crops?.[id];
  return !!c && (level ?? 1) >= c.level;
}

// Flower colours (Kwiaty §7): the dots on a card, and the colour picked per flower — per device in
// localStorage (sd_crop_colors), with a session copy so a throwing storage still keeps the pick.
const COLORS_KEY = 'sd_crop_colors';
let sessionColors = {};
export function _resetColorsForTests() { sessionColors = {}; }
function readColors(storage) {
  try { return { ...JSON.parse(storage?.getItem(COLORS_KEY) ?? '{}'), ...sessionColors }; } catch { return { ...sessionColors }; }
}
export function colorDots(catalog, id, stars, picked) {
  return (catalog?.crops?.[id]?.colors ?? []).map((color, need) => ({ color, need, unlocked: stars >= need, picked: color === picked }));
}
export function pickedColor(catalog, id, stars, storage) {
  const colors = catalog?.crops?.[id]?.colors ?? [];
  if (!colors.length) return null;
  const want = readColors(storage)[id];
  const i = colors.indexOf(want);
  return i >= 0 && stars >= i ? want : colors[0];
}
export function savePickedColor(id, color, storage) {
  sessionColors = { ...sessionColors, [id]: color };
  try { storage?.setItem(COLORS_KEY, JSON.stringify({ ...readColors(storage), [id]: color })); } catch { /* the session copy holds it */ }
}

// The market's keys (owner 2026-10-02): ←/→ the previous / next page (no wrap), Esc closes, Tab / Shift+Tab the next /
// previous tab in TABS order (wrapping; a new tab starts on its first page). → {tab, page}, 'close', or null.
export function marketKey(key, shift, { tab, page, pages }) {
  if (key === 'Escape') return 'close';
  if (key === 'ArrowRight') return page + 1 < pages ? { tab, page: page + 1 } : null;
  if (key === 'ArrowLeft') return page > 0 ? { tab, page: page - 1 } : null;
  if (key === 'Tab') {
    const i = TABS.findIndex((t) => t.id === tab), n = TABS.length;
    return { tab: TABS[(i + (shift ? -1 : 1) + n) % n].id, page: 0 };
  }
  return null;
}

export function marketPage(catalog, { level = 1, mastery = {}, tab = 'wszystko', page = 0, selected = null, pageSize = PAGE_SIZE } = {}) {
  const crops = catalog?.crops ?? {};
  const ids = Object.keys(crops)
    .filter((id) => tab === 'wszystko' || crops[id].tab === tab)
    // Owner 2026-10-20: the ones you can plant first, quickest to longest; then the locked ones, next unlock first.
    .sort((a, b) => (level < crops[a].level) - (level < crops[b].level)
      || (level < crops[a].level ? crops[a].level - crops[b].level : 0)
      || crops[a].grow_seconds - crops[b].grow_seconds || a.localeCompare(b));
  const pages = Math.max(1, Math.ceil(ids.length / pageSize));
  // page null: the one with the selected seed (owner 2026-10-29: opening the Rynek always showed page 1)
  const start = page ?? Math.floor(Math.max(0, ids.indexOf(selected)) / pageSize);
  const p = Math.min(Math.max(0, start), pages - 1);
  const cards = ids.slice(p * pageSize, (p + 1) * pageSize).map((id) => cropCard(catalog, id, { level, mastery, selected }));
  return { cards, page: p, pages };
}

// One crop's card: the market list and the Siewnik's seed-badge tooltip both show it (owner 2026-10-06).
export function cropCard(catalog, id, { level = 1, mastery = {}, selected = null } = {}) {
  const c = catalog?.crops?.[id];
  if (!c) return null;
  const m = mastery[id] ?? { harvested: 0, stars: 0 };
  const t = catalog?.mastery?.thresholds?.[c.band];
  return {
    id, name: c.name_pl, locked: level < c.level, level: c.level, growSeconds: c.grow_seconds,
    seed: c.seed_price, sell: masteredSellPrice(c, m.stars, catalog), xp: c.xp, harvestXp: c.harvest_xp ?? 0, stars: m.stars,
    progress: t && m.stars < 3 ? { have: m.harvested, need: t[m.stars] } : null,
    selected: id === selected,
  };
}

// The card's footer: mastery stars and progress, or the level it unlocks at.
// `hint` (Kwiaty §7): a locked flower colour being previewed — the stars it needs, where the card shows its stars.
export function cropCardFoot(c, hint = null) {
  if (hint) return `od {star_full}${hint.need}`;
  const stars = '{star_full}'.repeat(c.stars) + '{star_empty}'.repeat(3 - c.stars);
  return c.locked ? `{lock} poz. ${c.level}` : `${stars}${c.progress ? `  ${c.progress.have}/${c.progress.need}` : ''}`;
}

// The Narzędzia tab: one card per vehicle — the next size to buy (or "Masz ✓" at 4×4) — and the
// fuel card. 'poor' = unlocked but unaffordable (an owned 2×2 still reads "Ulepsz do 4×4").
const TOOL_ORDER = ['tractor', 'seeder', 'harvester'];

export function toolCards(catalog, { level = 1, tools = {}, fuelUnits = 0, money = 0, cans = 0, konewka = false } = {}) {
  const cat = catalog?.tools ?? {};
  const cards = TOOL_ORDER.filter((id) => cat[id]).map((id) => {
    const owned = tools[id] ?? 0;
    const tier = cat[id].tiers.find((t) => t.size > owned);
    if (!tier) return { kind: 'tool', id, name: cat[id].name_pl, size: owned, price: null, level: null, state: 'owned', owned };
    const state = level < tier.level ? 'locked' : money < tier.price ? 'poor' : owned ? 'upgrade' : 'buy';
    return { kind: 'tool', id, name: cat[id].name_pl, size: tier.size, price: tier.price, level: tier.level, state, owned };
  });
  // The Konewka (owner 2026-10-27): bought once, any level; it waters 2×2 on a friend's farm
  const k = catalog?.konewka;
  if (k) cards.push({ kind: 'konewka', price: k.price, size: k.size, state: konewka ? 'owned' : money < k.price ? 'poor' : 'buy' });
  const f = catalog?.fuel;
  if (f) cards.push({ kind: 'fuel', units: Math.floor(fuelUnits), tank: f.tank, price: f.refill_cost,
    state: fuelUnits >= f.tank ? 'full' : money < f.refill_cost ? 'poor' : 'buy',
    // a friend's canister, only while it fits whole (no half-wasted gift)
    cans, canUsable: cans > 0 && fuelUnits <= f.tank - (f.can_units ?? 50) });
  return cards;
}

// The info lines on a crop card. "💰 1 → 5" read as a sum to the owner (2026-09-29): say it in
// words — what the seed costs, what the harvest sells for.
export function cropCardInfo(c) {
  // planting XP / harvest XP: "+3 / +14 XP" (owner 2026-10-20: no word "zbiór", it made the line too long)
  const xp = c.harvestXp ? `+${c.xp} / +${c.harvestXp} XP` : `+${c.xp} XP`;
  return `{timer} ${humanizeRemaining(c.growSeconds)}   {xp_star} ${xp}\nNasiona: ${c.seed} {coin}\nSprzedasz za: ${c.sell} {coin}`;
}

// Scale (<= 1) that makes a text `width` px wide fit inside `max` px: the info lines on the market
// cards and the seed tooltip are drawn at their normal size and only shrink when they would spill.
export const fitScale = (width, max) => (width > max && width > 0 ? max / width : 1);

// Dekoracje (decorations, Płot, Siano) and Budowle (the landmarks) — sub-project 4a.
const TAB_KINDS = { dekoracje: ['decoration', 'fence', 'hay'], budowle: ['landmark'], drzewa: ['tree'] };

// A tree card's info line (4b): how often it fruits and what a harvest pays.
// A game "day" is 23 h (like crops), so the cycle is counted in those days: "co 2d", not "1d 22h".
const TREE_DAY_S = 23 * 3600;
export const treeCardInfo = (o) => `owoce co ${Math.round(o.cycle_s / TREE_DAY_S)}d · {coin} ${o.payout}` +
  (o.harvest_xp ? ` · {xp_star} +${o.harvest_xp}` : '');

export function objectCards(raw, tab) {
  const objs = raw?.catalog?.farm?.objects ?? {};
  const kinds = TAB_KINDS[tab] ?? [];
  const owned = (id) => (raw?.farm?.storage?.[id] ?? 0) > 0 || (raw?.placements ?? []).some((p) => p.scene === 'farm' && p.item === id);
  return Object.entries(objs).filter(([, o]) => kinds.includes(o.kind))
    .sort(([, a], [, b]) => a.level - b.level || a.price - b.price)
    .map(([item, o]) => ({ item, name: o.name_pl, price: o.price, level: o.level, w: o.w,
      state: o.unique && owned(item) ? 'owned' : (raw?.level ?? 1) < o.level ? 'locked' : (raw?.money ?? 0) < o.price ? 'poor' : 'buy' }));
}

// Zwierzęta (4c): each hut followed by its animal. An animal with no free place in a placed hut
// still sells — it waits in the Schowek until a hut is placed.
const ANIMAL_PLURAL = {
  kura: (n) => plural(n, 'kurę', 'kury', 'kur'), krowa: (n) => plural(n, 'krowę', 'krowy', 'krów'),
};
const FOR_WHOM = { kura: 'kur', krowa: 'krów' };             // "Jedzenie dla kur" (owner 2026-10-01)
export function animalCards(raw) {
  const fc = raw?.catalog?.farm ?? {}; const objs = fc.objects ?? {};
  const level = raw?.level ?? 1, money = raw?.money ?? 0;
  const stateOf = (price, lvl) => (level < lvl ? 'locked' : money < price ? 'poor' : 'buy');
  const room = (hutId) => (raw?.placements ?? []).filter((p) => p.scene === 'farm' && p.item === hutId)
    .reduce((n, p) => n + objs[hutId].holds - (raw?.animals ?? []).filter((a) => a.scene === 'farm' && a.buda_placement_id === p.id).length, 0);
  const out = [];
  for (const [hutId, h] of Object.entries(objs).filter(([, o]) => o.kind === 'hut').sort(([, a], [, b]) => a.level - b.level)) {
    out.push({ kind: 'hut', item: hutId, name: h.name_pl, price: h.price, level: h.level, w: h.w, state: stateOf(h.price, h.level), info: `2×2 · mieści ${h.holds} ${ANIMAL_PLURAL[h.animal]?.(h.holds) ?? ''}`.trim() });
    const a = fc.animals?.[h.animal];
    if (a) out.push({ kind: 'animal', item: h.animal, name: a.name_pl, price: a.price, level: h.level, state: stateOf(a.price, h.level),
      info: room(hutId) > 0 ? `${a.produces === 'jajko' ? '{egg}' : '{milk}'} co 10–20 min` : `brak miejsca — postaw ${h.name_pl.toLowerCase()}` });
    // …and its food (owner 2026-10-01: it was only in the town's pet shop). Bought one at a time.
    const f = a && raw?.catalog?.foods?.[a.food_id];
    if (f) out.push({ kind: 'food', item: a.food_id, name: f.name_pl, price: f.price, level: 1,
      state: money < f.price ? 'poor' : 'buy', info: `Jedzenie dla ${FOR_WHOM[h.animal] ?? 'zwierząt'} · masz: ${raw?.inventory?.foods?.[a.food_id] ?? 0}` });
  }
  return out;
}
